import type { 
  GetPaymentsHistorySummaryReportData,
  GetPaymentsHistorySummaryReportResponse,

  GetPaymentsHistoryOutsidersReportData,
  GetPaymentsHistoryOutsidersReportResponse,

  GetPaymentsHistoryData,
  GetPaymentsHistoryResponse,

  GetPaymentsHistoryPayoutSummaryData,
  GetPaymentsHistoryPayoutSummaryResponse,

  GetPaymentsHistoryCurrenciesResponse,

  GetPaymentsHistoryByIdData,
  GetPaymentsHistoryByIdResponse,

  PostPaymentsHistoryByIdFilesData,
  PostPaymentsHistoryByIdFilesResponse,

  GetPaymentsHistoryByIdCpaPlayersCountData,
  GetPaymentsHistoryByIdCpaPlayersCountResponse,

  GetPaymentsHistoryByIdCpaPlayersData,
  GetPaymentsHistoryByIdCpaPlayersResponse,

  PostPaymentsHistoryByIdApproveData,
  PostPaymentsHistoryByIdApproveResponse,

  PostPaymentsHistoryByIdManualPayoutData,
  PostPaymentsHistoryByIdManualPayoutResponse,

  PostPaymentsHistoryByIdRejectData,
  PostPaymentsHistoryByIdRejectResponse,

  PostPaymentsHistoryByIdReturnToApproveData,
  PostPaymentsHistoryByIdReturnToApproveResponse,

  PostPaymentsHistoryByIdToReworkData,
  PostPaymentsHistoryByIdToReworkResponse,
  
  PostPaymentsHistoryByIdReworkedData,
  PostPaymentsHistoryByIdReworkedResponse,

  PostPaymentsHistoryByIdChargeCpaRevenueData,
  PostPaymentsHistoryByIdChargeCpaRevenueResponse,

  PostPaymentsHistoryByIdMassChargeCpaRevenueData,
  PostPaymentsHistoryByIdMassChargeCpaRevenueResponse,

  GetCpaConversionsData,
  GetCpaConversionsResponse,
  GetCpaConversionsByIncrementingIdCpaPayoutsResponse,
  PostCpaConversionsByIncrementingIdManualPayoutData,
  PostCpaConversionsByIncrementingIdManualPayoutResponse,

  PostPaymentsHistoryExternalData,
  PostPaymentsHistoryExternalResponse,

  PostPaymentsHistoryByIdCancelExternalData,
  PostPaymentsHistoryByIdCancelExternalResponse,
} from "@/types/openapi-ts";
import type { AxiosInstance } from "axios";

export function getPaymentsApiModule(affiliatesApi: AxiosInstance) {
  return {
    getSummaryReport(params: GetPaymentsHistorySummaryReportData['query']) {
      return affiliatesApi.get<GetPaymentsHistorySummaryReportResponse>('/payments-history/summary-report', { params });
    },
    getOutsidersReport(params: GetPaymentsHistoryOutsidersReportData['query']) {
      return affiliatesApi.get<GetPaymentsHistoryOutsidersReportResponse>(
        '/payments-history/outsiders-report', 
        { params }
      );
    },
    getPaymentsHistory(params: GetPaymentsHistoryData['query']) {
      return affiliatesApi.get<GetPaymentsHistoryResponse>('/payments-history', { params });
    },
    getPaymentsHistoryPayoutSummary(params: GetPaymentsHistoryPayoutSummaryData['query']) {
      return affiliatesApi.get<GetPaymentsHistoryPayoutSummaryResponse>(
        '/payments-history/payout-summary',
        { params },
      );
    },
    getPaymentsHistoryCurrencies() {
      return affiliatesApi.get<GetPaymentsHistoryCurrenciesResponse>('/payments-history/currencies');
    },
    getPaymentInfo(id: string) {
      return affiliatesApi.get<GetPaymentsHistoryByIdResponse>(`/payments-history/${id}`);
    },
    postPaymentsHistoryExternal(payload: PostPaymentsHistoryExternalData['body']) {
      return affiliatesApi.post<PostPaymentsHistoryExternalResponse>('/payments-history/external', payload);
    },
    postPaymentInfoFiles(id: string, fileIds: string[]) {
      const data: PostPaymentsHistoryByIdFilesData['body'] =  {
        file_ids: fileIds
      };

      return affiliatesApi.post<PostPaymentsHistoryByIdFilesResponse>(`/payments-history/${id}/files`, data);
    },
    getPaymentInfoPlayersCount(id: string, params: GetPaymentsHistoryByIdCpaPlayersCountData['query']) {
      return affiliatesApi.get<GetPaymentsHistoryByIdCpaPlayersCountResponse>(
        `/payments-history/${id}/cpa-players/count`, 
        { params }
      );
    },
    getPaymentInfoPlayers(id: string, params: GetPaymentsHistoryByIdCpaPlayersData['query']) {
      return affiliatesApi.get<GetPaymentsHistoryByIdCpaPlayersResponse>(`/payments-history/${id}/cpa-players`, { params });
    },
    approvePayment(id: string) {
      return affiliatesApi.post<PostPaymentsHistoryByIdApproveResponse>(`/payments-history/${id}/approve`);
    },
    manualPayout(id: string) {
      return affiliatesApi.post<PostPaymentsHistoryByIdManualPayoutResponse>(`/payments-history/${id}/manual-payout`);
    },
    declinePayment(id: string) {
      return affiliatesApi.post<PostPaymentsHistoryByIdRejectResponse>(`/payments-history/${id}/reject`);
    },
    cancelExternalPayment(id: string, payload: PostPaymentsHistoryByIdCancelExternalData['body']) {
      return affiliatesApi.post<PostPaymentsHistoryByIdCancelExternalResponse>(
        `/payments-history/${id}/cancel-external`,
        payload,
      );
    },
    reviewPayment(id: string) {
      return affiliatesApi.post<PostPaymentsHistoryByIdReturnToApproveResponse>(`/payments-history/${id}/return-to-approve`);
    },
    reworkPayment(id: string) {
      return affiliatesApi.post<PostPaymentsHistoryByIdToReworkResponse>(`/payments-history/${id}/to-rework`);
    },
    reworkedPayment(id: string) {
      return affiliatesApi.post<PostPaymentsHistoryByIdReworkedResponse>(`/payments-history/${id}/reworked`);
    },
    chargePaymentCpaRevenue(id: string, playerId: string) {
      const params: PostPaymentsHistoryByIdChargeCpaRevenueData['body'] = {
        player_id: playerId,
      }
      return affiliatesApi.post<PostPaymentsHistoryByIdChargeCpaRevenueResponse>(
        `/payments-history/${id}/charge-cpa-revenue`, 
        params
      );
    },
    massChargePaymentCpaRevenue(id: string, playerId: string) {
      const params: PostPaymentsHistoryByIdMassChargeCpaRevenueData['body'] = {
        player_id: playerId,
      }
      return affiliatesApi.post<PostPaymentsHistoryByIdMassChargeCpaRevenueResponse>(
        `/payments-history/${id}/mass-charge-cpa-revenue`, 
        params
      );
    },
    getCpaConversions(params: GetCpaConversionsData['query']) {
      return affiliatesApi.get<GetCpaConversionsResponse>('/cpa-conversions', { params });
    },
    getCpaConversionPayouts(id: number) {
      return affiliatesApi.get<GetCpaConversionsByIncrementingIdCpaPayoutsResponse>(`/cpa-conversions/${id}/cpa-payouts`);
    },
    postCpaConversionManualPayout(id: number, payload: PostCpaConversionsByIncrementingIdManualPayoutData['body']) {
      return affiliatesApi.post<PostCpaConversionsByIncrementingIdManualPayoutResponse>(
        `/cpa-conversions/${id}/manual-payout`,
        payload,
      );
    },
  }
}

